import serverName from '../serverName';
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import logo from "../logo.svg";
import { useAuth } from "../AuthContext";

export default function Header() {
    const [visible, setVisible] = useState(false);
    const [open, setOpen] = useState(false);
    const { me, loading, login, logout } = useAuth();

    const isAdmin = !!(me && me.roles && me.roles.includes("admin"));
    const isLogged = !!me;

    const itemKeys = useMemo(() => {
        const items = [
            {
                key: "pravidla",
                to: "/pravidla",
                external: false,
                label: (
                    <div className="flex items-center gap-2">
                        <i className="fox-icon fox-icon-parchment"></i>
                        Pravidla
                    </div>
                )
            },
            {
                key: "whitelist",
                to: "/whitelist",
                external: false,
                label: (
                    <div className="flex items-center gap-2">
                        <i className="fox-icon fox-icon-check-line"></i>
                        Whitelist
                    </div>
                )
            },
            {
                key: "admins",
                to: "/admins",
                external: false,
                label: (
                    <div className="flex items-center gap-2">
                        <i className="fox-icon fox-icon-users"></i>
                        Team
                    </div>
                )
            },
            {
                key: "discord",
                to: "https://discord.gg/DKQStqFhTs",
                external: true,
                label: (
                    <div className="flex items-center gap-2">
                        <i className="fox-icon fox-icon-discord"></i>
                        Discord
                    </div>
                )
            },
            {
                key: "gallery",
                to: "/gallery",
                external: false,
                label: (
                    <div className="flex items-center gap-2">
                        <i className="fox-icon fox-icon-camera-2"></i>
                        Galerie
                    </div>
                )
            }
        ];

        if (isLogged) {
            items.splice(3, 0, {
                key: "tickets",
                to: "/tickets",
                external: false,
                label: (
                    <div className="flex items-center gap-2">
                        <i className="fox-icon fox-icon-mail"></i>
                        Tikety
                    </div>
                )
            });
        }

        if (isAdmin) {
            items.push({
                key: "_edit_rules",
                to: "/_edit_rules",
                external: false,
                label: (
                    <div className="flex items-center gap-2 text-amber-300">
                        <i className="fox-icon fox-icon-gears"></i>
                        Úprava pravidel
                    </div>
                )
            });
        }

        return items;
    }, [isAdmin, isLogged]);

    const openMenu = () => {
        setVisible(true);
        requestAnimationFrame(() => setOpen(true));
    };

    const closeMenu = () => {
        setOpen(false);
        setTimeout(() => setVisible(false), 350);
    };

    useEffect(() => {
        const onEsc = e => { if (e.key === "Escape") closeMenu(); };
        if (visible) window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [visible]);

    const delaysOpen = itemKeys.map((_, i) => 200 + i * 80);
    const delaysClose = [...delaysOpen].reverse();

    return (
        <header className="border-b border-white/10 relative">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
                <Link to="/" onClick={closeMenu}>
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Amnesia logo" className="w-10 h-10 object-contain" />
                        <span className="text-xl font-semibold tracking-wide">{serverName} RP</span>
                    </div>
                </Link>

                <nav className="hidden sm:flex items-center gap-6">
                    <Link to="/pravidla" className="text-sm hover:opacity-80">
                        <i className="fox-icon fox-icon-parchment"></i> Pravidla
                    </Link>
                    <Link to="/whitelist" className="text-sm hover:opacity-80">
                        <i className="fox-icon fox-icon-check-line"></i> Whitelist
                    </Link>
                    <Link to="/admins" className="text-sm hover:opacity-80">
                        <i className="fox-icon fox-icon-users"></i> Team
                    </Link>
                    {isLogged && (
                        <Link to="/tickets" className="text-sm hover:opacity-80">
                            <i className="fox-icon fox-icon-mail"></i> Tikety
                        </Link>
                    )}
                    <a
                        href="https://discord.gg/DKQStqFhTs"
                        className="text-sm hover:opacity-80"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="fox-icon fox-icon-discord"></i> Discord
                    </a>
                    <Link to="/gallery" className="text-sm hover:opacity-80">
                        <i className="fox-icon fox-icon-camera-2"></i> Galerie
                    </Link>
                    {isAdmin && (
                        <Link to="/_edit_rules" className="text-sm hover:opacity-80 text-amber-300">
                            <i className="fox-icon fox-icon-gears"></i> Úprava pravidel
                        </Link>
                    )}
                </nav>

                <div className="hidden sm:flex items-center gap-3">
                    {loading ? (
                        <div className="text-xs text-white/60">Načítám…</div>
                    ) : me ? (
                        <>
                            <img src={me.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            <div className="text-sm text-white/90 max-w-[140px] truncate">
                                {me.display || me.username}
                            </div>
                            <button
                                onClick={logout}
                                className="px-3 py-1.5 rounded-lg border border-white/15 text-xs hover:bg-white/5"
                            >
                                Odhlásit
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={login}
                            className="px-4 py-2 rounded-lg bg-white text-black text-xs font-semibold hover:opacity-90 flex items-center gap-2"
                        >
                            <i className="fox-icon fox-icon-discord"></i>
                            Přihlásit se
                        </button>
                    )}
                </div>

                <button
                    className="sm:hidden text-white text-2xl"
                    onClick={open ? closeMenu : openMenu}
                    aria-label="Menu"
                >
                    <i className="fox-icon fox-icon-menu"></i>
                </button>
            </div>

            {visible && (
                <div className="fixed inset-0 z-50">
                    <div
                        className={`absolute inset-0 ${open ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
                        onClick={closeMenu}
                        aria-hidden="true"
                        style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                    />
                    <aside
                        className={`absolute top-0 right-0 h-full w-[84%] max-w-xs bg-black border-l border-white/10
                                    transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}
                                    flex flex-col`}
                    >
                        <div className="px-5 py-4 flex items-center justify-between border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <img src={logo} alt="Amnesia logo" className="w-8 h-8 object-contain" />
                                <span className="text-lg font-semibold">{serverName} RP</span>
                            </div>
                            <button className="text-white text-2xl" onClick={closeMenu} aria-label="Zavřít">
                                <i className="fox-icon fox-icon-menu"></i>
                            </button>
                        </div>

                        <nav className="flex flex-col px-5 py-4 gap-2">
                            {itemKeys.map((it, idx) => {
                                const delay = open ? delaysOpen[idx] : delaysClose[idx];
                                const base = "block text-base py-3 border-b border-white/10 hover:opacity-80";
                                const cls = `${base} transform transition-all duration-300 ${
                                    open ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                                }`;
                                const style = { transitionDelay: `${delay}ms` };
                                return it.external ? (
                                    <a
                                        key={it.key}
                                        href={it.to}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={cls}
                                        style={style}
                                        onClick={closeMenu}
                                    >
                                        {it.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={it.key}
                                        to={it.to}
                                        className={cls}
                                        style={style}
                                        onClick={closeMenu}
                                    >
                                        {it.label}
                                    </Link>
                                );
                            })}
                        </nav>

                        <div className="mt-auto px-5 py-4 border-t border-white/10">
                            {loading ? (
                                <div className="text-xs text-white/60">Načítám…</div>
                            ) : me ? (
                                <div className="flex items-center gap-3">
                                    <img src={me.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    <div className="flex-1">
                                        <div className="text-sm text-white/90 truncate">
                                            {me.display || me.username}
                                        </div>
                                    </div>
                                    <button
                                        onClick={logout}
                                        className="px-3 py-1.5 rounded-lg border border-white/15 text-xs hover:bg_white/5"
                                    >
                                        Odhlásit
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={login}
                                    className="w-full px-4 py-2 rounded-lg bg_white text-black text-sm font-semibold hover:opacity-90 flex items-center justify-center gap-2"
                                >
                                    <i className="fox-icon fox-icon-discord"></i>
                                    Přihlásit se
                                </button>
                            )}
                            <div className="mt-4 text-xs text-white/50">
                                © {new Date().getFullYear()} {serverName} RP
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </header>
    );
}
